import {
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import * as d3 from 'd3';

export type SupportedScale =
  | d3.ScaleBand<string>
  | d3.ScaleLinear<number, number>
  | d3.ScaleTime<number, number>
  | d3.ScaleLogarithmic<number, number>
  | d3.ScalePoint<string>;

@Directive({
  selector: '[bshScale]',
  standalone: true,
  exportAs: 'bshScale',
})
export class AxisScaleDirective implements OnChanges {
  @Input() type: 'band' | 'linear' | 'time' | 'log' | 'point' = 'band';
  @Input() domain: readonly (string | number | Date)[] = [];
  @Input() range: [number, number] = [0, 1];
  @Input() colorRange: readonly string[] = d3.schemeSet2;
  @Input() padding = 0.1;

  @Output() scaleChange = new EventEmitter<SupportedScale>();

  scale!: SupportedScale;

  ngOnChanges() {
    switch (this.type) {
      case 'band':
        this.scale = d3
          .scaleBand<string>()
          .domain(this.domain as string[])
          .range(this.range)
          .padding(this.padding);
        break;
      case 'point':
        this.scale = d3
          .scalePoint<string>()
          .domain(this.domain as string[])
          .range(this.range)
          .padding(this.padding);
        break;
      case 'linear':
        this.scale = d3
          .scaleLinear()
          .domain(this.domain as number[])
          .range(this.range);
        break;
      case 'time':
        this.scale = d3
          .scaleTime()
          .domain(this.domain as [Date, Date])
          .range(this.range);
        break;
      case 'log':
        this.scale = d3
          .scaleLog()
          .domain(this.domain as number[])
          .range(this.range);
        break;
      default:
        throw new Error(`Unsupported scale type: ${this.type}`);
    }
    this.scaleChange.emit(this.scale);
  }

  get bandwidth(): number {
    return 'bandwidth' in this.scale
      ? ((
          this.scale as d3.ScaleBand<string> | d3.ScalePoint<string>
        ).bandwidth?.() ?? 0)
      : 0;
  }

  ticks(): (number | Date)[] {
    if ('ticks' in this.scale && typeof this.scale.ticks === 'function') {
      return this.scale.ticks();
    }
    return [];
  }

  position(value: string | number | Date): number {
    if (typeof this.scale === 'function') {
      return (
        (this.scale as (v: string | number | Date) => number | undefined)(
          value
        ) ?? 0
      );
    }
    return 0;
  }
}
